import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { Component, h, Method, Prop, State, Watch } from "@stencil/core"
import { HorizontalMarkSizeEnum, HorizontalMarkSizeEnumsObj } from "../../enums/HorizontalMarkSizeEnum"
import { PaymentMethodEnum, PaymentMethodEnumsObj } from "../../enums/PaymentMethodEnum"
import { srcScreenError } from "../../helpers/CommonRenders"
import { validatePaymentMethodEnum } from "../../helpers/Validators"
import { getLanguageHandler, getLocale, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import { CardValidationModel } from "../../models/CardValidationModel"
import { CardHolderInfo } from "../commonCardInput/CardHolderInfo"
import { CardScreenMessageController } from "../commonCardInput/CardScreenMessageController"
import { CardScreenStateModel } from "../commonCardInput/CardScreenStateModel"
import { ConsumerInfoFormConfig } from "../consumerInfoForm/ConsumerInfoFormConfig.model"
import { ConsumerInfoFormError, ConsumerInfoFormValidity } from "../consumerInfoForm/ConsumerInfoFormValidity.model"
import { ConsumerInfoValue, createEmptyConsumerInfoValue } from "../consumerInfoForm/ConsumerInfoValue.model"
import { InfoMessageModel, MessageMaxLength } from "../infoMessage/InfoMessageModel"

const DEFAULT_UNEXPECTED_ERROR = "Something went wrong. Please try again."
const ADD_CARD_PAYMENT_METHODS: PaymentMethodEnum[] = [
  PaymentMethodEnumsObj.MC,
  PaymentMethodEnumsObj.VISA,
  PaymentMethodEnumsObj.MC_VISA,
]

type TwoStepCardFlowElement = HTMLTwoStepCardFlowElement & {
  returnToCardStep: () => Promise<void>
  scrollToTop: () => Promise<boolean>
}

/** Entire screen for the Returning User validate-first and encrypt-later Add Card flow. */
@Component({
  tag: "add-card-screen",
  styleUrl: "../commonCardInput/CardInputScreen.scss",
})
export class AddCardScreen {
  /** Merchant site preferred locale. Supported values: en_GB, sv_SE and pl_PL. */
  @Prop() locale?: string
  /** This flow currently supports `master`, `visa`, or `master,visa`. */
  @Prop() paymentMethod: PaymentMethodEnum
  /** Alert displayed by the Add Card screen. */
  @Prop() infoMessage?: InfoMessageModel
  /** Cardholder and billing data passed to card encryption. */
  @Prop() cardHolderInfo?: CardHolderInfo
  /** Current network-neutral controlled Consumer draft. */
  @Prop() consumerInfoValue: ConsumerInfoValue = createEmptyConsumerInfoValue()
  /** Network-neutral validation configuration derived by the flow owner. */
  @Prop() consumerInfoConfig?: ConsumerInfoFormConfig
  /** Recoverable external Consumer field errors from the selected mapper. */
  @Prop() consumerInfoErrors: ConsumerInfoFormError[] = []
  /** Callback receiving complete user-originated Consumer candidates. */
  @Prop() consumerInfoOnChange?: (value: ConsumerInfoValue) => void
  /** Callback forwarding validity notifications from the live Consumer form. */
  @Prop() consumerInfoValidityOnChange?: (validity: ConsumerInfoFormValidity) => void
  /** Indicates whether Add Card processing is in progress. */
  @Prop() isLoading?: boolean
  /** Environment used for card encryption. */
  @Prop() env: "QA" | "PROD"
  /** PSP name used for card encryption. */
  @Prop() psp: string
  /** Optional first-step description override. */
  @Prop() descriptionHeader?: string
  /** Hides the action that returns to Card List. */
  @Prop() hideSelectCardButton?: boolean
  /** Recognized user's masked credential displayed by the switch-user control. */
  @Prop() userCredentials: string
  /** Transaction amount retained for backward compatibility. */
  @Prop() amount?: string
  /** Transaction currency retained for backward compatibility. */
  @Prop() currency?: string
  /** Callback called after the close button is selected. */
  @Prop() closeButtonOnClick?: () => void
  /** Callback called once after the final encrypted-card response. */
  @Prop() addCardButtonOnClick?: (cardScreenState: CardScreenStateModel) => void | Promise<void>
  /** Callback called after Select card is selected. */
  @Prop() selectCardButtonOnClick?: () => void
  /** Callback called after the recognized user selects Not you. */
  @Prop() notYouOnClick?: () => void
  /** Callback invoked after validate-only card recognition. */
  @Prop() cardValidationOnChange?: (validation: CardValidationModel) => boolean | Promise<boolean>
  /** Callback invoked for an invalid, unrecognized or unsupported card. */
  @Prop() invalidCardOnChange?: (validation: CardValidationModel) => void
  /** Pre-encryption Consumer mapping and validation guard. */
  @Prop() prepareStoreAndPay?: (cardBrand: "MASTERCARD" | "VISA") => boolean | Promise<boolean>

  @State() displayedInfoMessage?: InfoMessageModel

  private languageHandler: LanguageHandler
  private supportedLocale: string
  private horizontalMarkSize: HorizontalMarkSizeEnum
  private errorMessage: string[] = []
  private pendingProcessingErrorScroll = false
  private twoStepCardFlow?: TwoStepCardFlowElement
  private messageController = new CardScreenMessageController(message => (this.displayedInfoMessage = message))

  componentWillLoad() {
    if (!this.paymentMethod) {
      this.errorMessage.push("'paymentMethod' prop is not defined")
    } else if (!validatePaymentMethodEnum(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not valid")
    } else if (!ADD_CARD_PAYMENT_METHODS.includes(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not supported by this flow")
    }
    if (this.env !== "PROD" && this.env !== "QA") {
      this.errorMessage.push("'env' param must be 'QA' or 'PROD'")
    }
    if (typeof this.psp !== "string" || !this.psp.trim()) {
      this.errorMessage.push("'psp' param must be a non-empty string")
    }
    if (this.errorMessage.length > 0) {
      return
    }

    const localeData = getSupportedLocaleData(this.locale)
    this.supportedLocale = getLocale(localeData)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.horizontalMarkSize = HorizontalMarkSizeEnumsObj.S16

    const initialInfoMessage =
      this.infoMessage && this.infoMessage.infoMessage.length > MessageMaxLength.ADD_CARD_SCREEN
        ? {
            ...this.infoMessage,
            infoMessage: this.infoMessage.infoMessage.substring(0, MessageMaxLength.ADD_CARD_SCREEN),
          }
        : this.infoMessage
    this.messageController.setExternalMessage(initialInfoMessage)
  }

  @Watch("infoMessage")
  infoMessageChanged(message?: InfoMessageModel) {
    if (message?.infoType === "ERROR" && message.infoMessage) {
      this.pendingProcessingErrorScroll = true
    }
    this.messageController.setExternalMessage(message)
  }

  private showUnexpectedAttemptError = () => {
    this.pendingProcessingErrorScroll = true
    this.messageController.showLocalError(
      this.languageHandler.getString("unexpectedError") || DEFAULT_UNEXPECTED_ERROR
    )
  }

  private clearRecoverableActionError = () => {
    this.messageController.clearActionError()
  }

  componentDidRender() {
    if (!this.pendingProcessingErrorScroll) {
      return
    }

    this.pendingProcessingErrorScroll = false
    void this.twoStepCardFlow?.scrollToTop()
  }

  /** Returns the flow to the card-entry step after a recoverable card error. */
  @Method()
  async returnToCardStep(): Promise<void> {
    await this.twoStepCardFlow?.returnToCardStep()
  }

  private handleCardValidation = async (validation: CardValidationModel): Promise<boolean> => {
    this.messageController.beginAttempt()
    return (await this.cardValidationOnChange?.(validation)) !== false
  }

  private handleInvalidCard = (validation: CardValidationModel) => {
    this.messageController.beginAttempt()
    this.invalidCardOnChange?.(validation)
  }

  private handlePrepareStoreAndPay = async (cardBrand: "MASTERCARD" | "VISA"): Promise<boolean> => {
    this.messageController.beginAttempt()
    return (await this.prepareStoreAndPay?.(cardBrand)) !== false
  }

  private renderCardStepHeader() {
    return (
      <div slot="card-step-header">
        <div class="greetingText">{this.languageHandler.getString("addNewCard")}</div>
        <div class="cardInputDescriptionText">
          {this.descriptionHeader || this.languageHandler.getString("fastSecureOnlineCheckout")}
        </div>
        <div class="horizontalMarkContainer">
          <span class="horizontalMarkText">{this.languageHandler.getString("weAccept")}</span>
          <horizontal-mark
            locale={this.supportedLocale}
            paymentMethod={this.paymentMethod}
            horizontalMarkSize={this.horizontalMarkSize}
          />
        </div>
        {this.userCredentials && (
          <div class="switchUserComponent">
            <switch-user
              locale={this.supportedLocale}
              userCredentials={this.userCredentials}
              changeUserButtonOnClick={this.notYouOnClick}
            />
          </div>
        )}
      </div>
    )
  }

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("Add Card Screen", this.errorMessage)
    }

    return (
      <div class="cardScreen consumerCardScreen">
        <div class="closeButtonContainer">
          {typeof this.closeButtonOnClick === "function" && (
            <close-button closeButtonOnClick={this.closeButtonOnClick} />
          )}
        </div>
        <div class="cardScreenContent">
          <div class="consumerCardScrollableContent">
            <two-step-card-flow
              ref={element => (this.twoStepCardFlow = element as TwoStepCardFlowElement)}
              locale={this.supportedLocale}
              paymentMethod={this.paymentMethod}
              env={this.env}
              psp={this.psp}
              cardHolderInfo={this.cardHolderInfo}
              consumerInfoValue={this.consumerInfoValue}
              consumerInfoConfig={this.consumerInfoConfig}
              consumerInfoErrors={this.consumerInfoErrors}
              showRememberMe={false}
              isLoading={this.isLoading === true}
              infoMessage={this.displayedInfoMessage}
              consumerInfoOnChange={this.consumerInfoOnChange}
              consumerInfoValidityOnChange={this.consumerInfoValidityOnChange}
              cardValidationOnChange={this.handleCardValidation}
              invalidCardOnChange={this.handleInvalidCard}
              prepareStoreAndPay={this.handlePrepareStoreAndPay}
              unexpectedErrorOnChange={this.showUnexpectedAttemptError}
              goBackOnChange={this.clearRecoverableActionError}
              encryptedCardOnChange={cardState => this.addCardButtonOnClick?.(cardState)}>
              {this.renderCardStepHeader()}
              {!this.hideSelectCardButton && (
                <button
                  slot="card-step-footer"
                  class="selectCardTextButton"
                  type="button"
                  onClick={this.selectCardButtonOnClick}>
                  {this.languageHandler.getString("selectCardButton")}
                </button>
              )}
            </two-step-card-flow>
          </div>
        </div>
      </div>
    )
  }
}
